"use client"

import { FC, useRef } from "react"
import { Content } from "@prismicio/client"
import { PrismicRichText, SliceComponentProps } from "@prismicio/react"
import { PrismicNextImage } from "@prismicio/next"
import {
  LazyMotion,
  domAnimation,
  m,
  motion,
  type Variants,
  useScroll,
  useTransform,
} from "motion/react"
import { vFadeUp, vFadeIn, vStaggerContainer } from "@/lib/animations"

export type CoverTitleSubtitleProps =
  SliceComponentProps<Content.CoverTitleSubtitleSlice>

const vIconDrop: Variants = {
  hidden: { opacity: 0, y: -24, scale: 0.85 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.9, ease: [0.34, 1.56, 0.64, 1] as const },
  },
}

const CoverTitleSubtitle: FC<CoverTitleSubtitleProps> = ({ slice }) => {
  const { primary } = slice
  const sectionRef = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  })
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"])

  return (
    <LazyMotion features={domAnimation}>
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        ref={sectionRef}
        aria-label={slice.slice_type
          .split("_")
          .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ")}
        className="relative flex min-h-[84vh] items-center justify-center overflow-hidden"
      >
        {/* Background image with parallax + subtle zoom-in on load */}
        {primary.background_image?.url && (
          <motion.div
            className="absolute inset-0 will-change-transform"
            initial={{ scale: 1.06 }}
            animate={{ scale: 1 }}
            style={{ y: bgY }}
            transition={{ duration: 2.5, ease: "easeOut" }}
          >
            <PrismicNextImage
              field={primary.background_image}
              fallbackAlt=""
              fill
              className="object-cover object-center"
              priority
              fetchPriority="high"
              imgixParams={{ auto: ["format", "compress"], q: 75 }}
            />
          </motion.div>
        )}

        {/* Multi-layer overlay: bottom-up gradient + warm tint */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to top, oklch(0.15 0.065 18 / 0.95) 0%, oklch(0.21 0.085 22 / 0.75) 40%, oklch(0.59 0.17 42 / 0.45) 100%)",
          }}
        />
        {/* Vignette edges */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 120% 100% at 50% 50%, transparent 40%, oklch(0.1 0.04 20 / 0.6) 100%)",
          }}
        />

        {/* Floating gold particles */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-hidden"
        >
          {[...Array(8)].map((_, n) => (
            <motion.div
              key={n}
              className="absolute size-1 rounded-full"
              style={{
                background: "var(--sacred-gold)",
                left: `${10 + n * 11}%`,
                top: `${15 + (n % 4) * 20}%`,
              }}
              animate={{ y: [-10, 10, -10], opacity: [0.1, 0.35, 0.1] }}
              transition={{
                duration: 3.5 + n * 0.6,
                repeat: Infinity,
                ease: "easeInOut",
                delay: n * 0.35,
              }}
            />
          ))}
        </div>

        {/* Content */}
        <m.div
          className="relative z-10 container section-my flex flex-col items-center gap-6 section-py text-center"
          variants={vStaggerContainer}
          initial="hidden"
          animate="visible"
        >
          {/* Icon */}
          {primary.icon?.url && (
            <m.div variants={vIconDrop} className="relative">
              {/* Pulse rings */}
              {[1, 2].map((n) => (
                <motion.div
                  key={n}
                  className="absolute rounded-full border border-white/30"
                  initial={{ width: 96, height: 96, opacity: 0.5 }}
                  animate={{
                    width: 96 + n * 40,
                    height: 96 + n * 40,
                    opacity: 0,
                  }}
                  transition={{
                    duration: 2.2,
                    repeat: Infinity,
                    ease: "easeOut",
                    delay: n * 0.8,
                  }}
                  style={{
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                  }}
                />
              ))}
              {/* Gold halo */}
              <div
                className="absolute inset-0 scale-[1.8] rounded-full blur-2xl"
                style={{ background: "var(--sacred-gold)", opacity: 0.3 }}
              />
              <div className="relative size-24 overflow-hidden rounded-full shadow-2xl ring-2 ring-white/30">
                <PrismicNextImage
                  field={primary.icon}
                  fallbackAlt=""
                  width={100}
                  height={100}
                  className="size-full object-cover"
                  imgixParams={{ auto: ["format", "compress"], q: 75 }}
                />
              </div>
            </m.div>
          )}

          {/* Headline */}
          <m.div
            variants={vFadeUp}
            className="[&_h1]:text-gradient-amber [&_h2]:text-gradient-amber [&_h3]:text-gradient-amber [&_p]:text-white"
          >
            <PrismicRichText field={primary.headline} />
          </m.div>

          {/* Shimmer line */}
          <m.div
            variants={{
              hidden: { scaleX: 0, opacity: 0 },
              visible: {
                scaleX: 1,
                opacity: 1,
                transition: { duration: 1, ease: "easeOut", delay: 0.2 },
              },
            }}
            className="h-px w-32 origin-center rounded-full"
            style={{
              background:
                "linear-gradient(to right, transparent, var(--sacred-gold), transparent)",
            }}
          />

          {/* Subtitle 1 */}
          {primary.subtitle_1 && (
            <m.div
              variants={vFadeUp}
              className="max-w-2xl [&_h2]:text-white/80 [&_h3]:text-white/80 [&_p]:text-white/80"
            >
              <PrismicRichText field={primary.subtitle_1} />
            </m.div>
          )}

          {/* Subtitle 2 — frosted pill */}
          {primary.subtitle_2 && (
            <m.div
              variants={vFadeIn}
              className="max-w-xl overflow-hidden rounded-2xl p-px"
              style={{
                background:
                  "linear-gradient(135deg, oklch(1 0 0 / 0.25), oklch(1 0 0 / 0.08))",
              }}
            >
              <div
                className="rounded-2xl px-7 py-3 backdrop-blur-md [&_p]:text-white/90"
                style={{
                  background: "oklch(0.15 0.065 18 / 0.35)",
                }}
              >
                <PrismicRichText field={primary.subtitle_2} />
              </div>
            </m.div>
          )}
        </m.div>

        {/* Bottom fade-out edge */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-24"
          style={{
            background:
              "linear-gradient(to bottom, transparent, oklch(0.15 0.065 18 / 0.6))",
          }}
        />
      </section>
    </LazyMotion>
  )
}

export default CoverTitleSubtitle
